import { useEffect } from 'react';

import { ConstVoid, WebVitals } from '@catawiki/runtime-toolkit';

export const WEB_VITALS_DEFAULT_SAMPLE_RATE = 10; // 10% sampling rate for production
interface WebVitalsTrackerProps {
  sampleRate?: number; // Sampling rate as a percentage (0-100)
}

const sampleSizeRandomizer = (
  callback: ConstVoid,
  sampleSizePercent: number
) => {
  const randomNumber = parseFloat((Math.random() * 100).toFixed(1));

  if (randomNumber <= sampleSizePercent) {
    callback();
  }
};

type ConnectionInfo = {
  effectiveType?: unknown;
  saveData?: unknown;
} & Record<string, unknown>;

const getConnection = (): ConnectionInfo => {
  let connection: ConnectionInfo = {};

  if ('connection' in globalThis.window.navigator) {
    connection = globalThis.window.navigator.connection as ConnectionInfo;
  } else if ('mozConnection' in globalThis.window.navigator) {
    connection = globalThis.window.navigator.mozConnection as ConnectionInfo;
  } else if ('webkitConnection' in globalThis.window.navigator) {
    connection = globalThis.window.navigator.webkitConnection as ConnectionInfo;
  }

  return connection;
};

const getEffectiveConnectionType = (): string => {
  const connection = getConnection();
  const effectiveType = connection.effectiveType;

  return typeof effectiveType === 'string' ? effectiveType : 'unknown';
};

const getSaveData = (): string | number => {
  const connection = getConnection();
  const saveData = connection.saveData;

  if (typeof saveData === 'boolean') {
    return saveData ? 1 : 0;
  }

  if (typeof saveData === 'number' || typeof saveData === 'string') {
    return saveData;
  }

  return 'unknown';
};

const getPageWidth = () =>
  globalThis.window?.innerWidth ||
  globalThis.document.documentElement?.clientWidth ||
  globalThis.document.body.clientWidth;

const getPageHeight = () =>
  globalThis.window?.innerHeight ||
  globalThis.document?.documentElement?.clientHeight ||
  globalThis.document?.body.clientHeight;

const reportToGoogleAnalytics = ({
  delta,
  id,
  name,
  rating,
  value,
  attribution: attributionData,
}: WebVitals.MetricWithAttribution) => {
  const attribution = attributionData as Record<string, any>;

  const debugTarget = attribution
    ? attribution?.largestShiftTarget ||
      attribution?.element ||
      attribution?.eventTarget ||
      ''
    : '(not set)';

  const eventTime = attribution
    ? attribution?.largestShiftTime ||
      (attribution?.lcpEntry && attribution?.lcpEntry?.startTime) ||
      attribution?.eventTime ||
      ''
    : '';

  window.dataLayer?.push({
    event: 'web_vitals_metric',
    metric_name: name,
    metric_id: id,
    metric_value: value,
    metric_delta: delta,
    metric_rating: rating,
    debug_target: debugTarget,
    debug_event_type: attribution?.eventType || '',
    debug_timing: attribution?.loadState || '',
    event_time: eventTime,
    effective_connection_type: getEffectiveConnectionType(),
    save_data: getSaveData(),
    width: getPageWidth(),
    height: getPageHeight(),
  });
};

export const WebVitalsTracker = ({
  sampleRate = WEB_VITALS_DEFAULT_SAMPLE_RATE,
}: WebVitalsTrackerProps = {}) => {
  useEffect(() => {
    sampleSizeRandomizer(() => {
      WebVitals.reportWebVitals((metric) => {
        reportToGoogleAnalytics(metric);
      });
    }, sampleRate);
  }, [sampleRate]);

  // This component doesn't render anything
  return null;
};
